import { Button, Col, Form, Row, Space, theme } from "antd";
import { useEffect, useState } from "react";
import Data from "../../controls/Data";
import { SearchQuery } from "../../code/search-query/search-query";
import { kpisViewColumns, mapKpisViewQuery } from "./KpisView.default";
import { getKpis } from "../../repository/data.repository";
import { createGridRequest } from "../../hooks/useGridRequest";
import DynamicForm from "../../controls/DynamicForm";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";
import { Kpi, KpiType, KpiScoringMethodType } from "../../repository/data.default";

export interface KpisViewProps {
  readonly trigger?: string;
  readonly onAction?: (key: string, record: any) => void;
  readonly onSearch?: (searchQuery: SearchQuery) => void;
}

function KpisView(props: KpisViewProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);

  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState<{
    readonly searchQuery: SearchQuery;
  }>({
    searchQuery: {
      conditions: [],
    },
  });
  const [valuesValid, setValuesValid] = useState(false);
  const permissions = useSelector(authorizeSelectors.permissions);

  // Lifecycle

  useEffect(() => {
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
  }, [values]);

  useEffect(() => {
    setContext({ ...context });
  }, [props.trigger]);

  // Events

  function onLoading(loading: boolean): void {
    setLoading(loading);
  }

  function onSearchData(): void {
    const searchQuery = mapKpisViewQuery(values);
    setContext({
      searchQuery,
    });
    props.onSearch?.(searchQuery);
  }

  function onClearData(): void {
    setValuesValid(true);
    form.resetFields();
    setContext({
      searchQuery: {
        conditions: [],
      },
    });
  }

  function onAction(key: string, record: any): void {
    props.onAction?.(key, record);
  }

  // Methods

  const request = createGridRequest(getKpis, { retry: true });

  return (
    <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
      {/* Form */}
      <Col
        className="mr-4 flex flex-col border border-solid pl-2 pr-2"
        style={{
          borderColor: token.colorBorder,
          borderRadius: token.borderRadius,
        }}
      >
        <Space className="flex flex-row justify-end pb-2 pt-2">
          <Button disabled={!valuesValid || loading} onClick={onClearData}>
            Clear
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={!valuesValid}
            onClick={onSearchData}
          >
            Search
          </Button>
        </Space>
        <DynamicForm
          className="w-72 overflow-y-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                  },
                  {
                    type: "textbox",
                    name: "shortDescription",
                    label: "Short Description",
                  },
                  {
                    type: "combobox",
                    name: "type",
                    label: "Type",
                    request: () =>
                      Promise.resolve(
                        Object.entries(KpiType).map(([value, display]) => ({
                          value,
                          display,
                        })),
                      ),
                  },
                  {
                    type: "combobox",
                    name: "scoringMethod",
                    label: "Scoring Method",
                    request: () =>
                      Promise.resolve(
                        Object.entries(KpiScoringMethodType).map(
                          ([value, display]) => ({
                            value,
                            display,
                          }),
                        ),
                      ),
                  },
                ],
              },
            ],
          }}
        />
      </Col>
      {/* Data */}
      <Data
        columns={kpisViewColumns}
        context={context}
        pageSize={100}
        // A KPI of a published or retired questionnaire version is frozen (the server refuses every write):
        // it is only viewed. An unowned KPI, or one of a draft, is edited as usual.
        actions={(record: Kpi) =>
          record?.questionnaireStatus && record.questionnaireStatus !== "Draft"
            ? [{ key: "view", label: "View", handler: onAction }]
            : [
                {
                  key: "update",
                  label: "Edit",
                  disabled: !permissions.can("KPIs", "edit"),
                  handler: onAction,
                },
                {
                  key: "delete",
                  label: "Delete",
                  danger: true,
                  disabled: !permissions.can("KPIs", "delete"),
                  handler: onAction,
                },
              ]
        }
        request={request}
        rowKey={({ uid }) => uid}
        onLoading={onLoading}
      />
    </Row>
  );
}

export default KpisView;
